import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { roleOf } from "@/lib/constants";
import AppShell from "@/components/AppShell";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AdminDashboard from "@/pages/AdminDashboard";
import Dashboard from "@/pages/Dashboard";
import Clinics from "@/pages/Clinics";
import Staff from "@/pages/Staff";
import Services from "@/pages/Services";
import Patients from "@/pages/Patients";
import PatientDetail from "@/pages/PatientDetail";
import Visits from "@/pages/Visits";
import Invoices from "@/pages/Invoices";
import NewInvoice from "@/pages/NewInvoice";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Expenses from "@/pages/Expenses";
import Reports from "@/pages/Reports";
import Inpatient from "@/pages/Inpatient";

export const homePath = (u) => (roleOf(u) === "super_admin" ? "/admin" : "/dashboard");

const Loading = () => <div className="grid h-screen place-items-center text-sm text-slate-400" data-testid="app-loading">Loading…</div>;

function Protected({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(roleOf(user))) return <Navigate to={homePath(user)} replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  return user ? <Navigate to={homePath(user)} replace /> : children;
}

const TENANT = ["ceo", "manager", "doctor", "nurse", "receptionist", "accountant", "dentist", "lab", "pharmacist", "hr"];
const CLINICAL = ["ceo", "manager", "doctor", "nurse", "receptionist", "dentist", "lab", "pharmacist", "accountant"];
const BILLING = ["ceo", "manager", "accountant", "receptionist", "doctor", "dentist"];
const FINANCE = ["ceo", "manager", "accountant"];

const guarded = (roles, el) => <Protected roles={roles}>{el}</Protected>;

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route element={<Protected><AppShell /></Protected>}>
              <Route path="/admin" element={guarded(["super_admin"], <AdminDashboard />)} />
              <Route path="/dashboard" element={guarded(TENANT, <Dashboard />)} />
              <Route path="/clinics" element={guarded(["ceo"], <Clinics />)} />
              <Route path="/staff" element={guarded(["ceo", "manager", "hr"], <Staff />)} />
              <Route path="/services" element={guarded(TENANT, <Services />)} />
              <Route path="/patients" element={guarded(CLINICAL, <Patients />)} />
              <Route path="/patients/:id" element={guarded(CLINICAL, <PatientDetail />)} />
              <Route path="/visits" element={guarded(CLINICAL, <Visits />)} />
              <Route path="/invoices" element={guarded(BILLING, <Invoices />)} />
              <Route path="/invoices/new" element={guarded(["manager", "accountant", "receptionist", "doctor", "dentist"], <NewInvoice />)} />
              <Route path="/invoices/:id" element={guarded(BILLING, <InvoiceDetail />)} />
              <Route path="/expenses" element={guarded(FINANCE, <Expenses />)} />
              <Route path="/reports" element={guarded(FINANCE, <Reports />)} />
              <Route path="/inpatient" element={guarded(TENANT, <Inpatient />)} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
